// Accounting — Chart of Accounts
//
// Lists the outlet's accounts, grouped the same way the desktop
// ChartOfAccount window groups them:
//   Assets / Liabilities / Owner's Equity / Revenue / Expenses → account type
//   → account rows.
//
// ET_Admin can add / rename / delete accounts from here. The gateway does the
// write through the desktop browser ops (SetBrowserNodeValue /
// DeleteBrowserNode) so entityFTS stays consistent; gain accounts stay
// read-only because the store has no write op for that category.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { AccountingAccountRef, AccountingAccountType } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

/** Desktop tree order (ChartOfAccountModel.cpp). */
const GROUP_ORDER = ['Assets', 'Liabilities', "Owner's Equity", 'Revenue', 'Expenses'];

interface CoaGroup {
  name: string;
  rows: AccountingAccountRef[];
}

interface KeptCoaView {
  groups: CoaGroup[];
}

interface AccountForm {
  /** Empty = create. */
  id: string;
  typeId: string;
  code: string;
  name: string;
}

@customElement('chart-of-account')
export class ChartOfAccount extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .head { display: flex; align-items: baseline; gap: 0.6rem; padding: 0.75rem 1rem 0.4rem; flex-shrink: 0; flex-wrap: wrap; }
    .head h2 { margin: 0; font-size: 1.02rem; }
    .head .hint { margin: 0; }
    .head .spacer { flex: 1; }
    .body { flex: 1; min-height: 0; overflow-y: auto; padding: 0 1rem 1rem; }
    .group { margin-top: 0.9rem; }
    .group h3 {
      margin: 0 0 0.4rem; font-size: 0.8rem; text-transform: uppercase;
      letter-spacing: 0.06em; color: var(--color-text-secondary);
    }
    .table-wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; background: var(--color-surface); }
    .code { font-variant-numeric: tabular-nums; }
    .acct .id { margin-left: 0.35rem; font-size: 0.72rem; color: var(--color-text-secondary); }
    .actions { display: flex; gap: 0.35rem; justify-content: flex-end; white-space: nowrap; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
    .error { margin: 0 1rem 0.5rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: none; align-items: center; justify-content: center; z-index: 60; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { width: min(520px, 100%); max-height: 90vh; overflow: auto; background: var(--color-surface); border-radius: 16px; box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3); }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-body { padding: 1rem 1.1rem; }
    .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .close { border: none; background: none; font-size: 1.35rem; cursor: pointer; color: var(--color-text-secondary); }
    .modal select { width: 100%; min-height: 40px; padding: 9px 12px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.86rem; }
    .modal .ro { padding: 9px 0; font-size: 0.86rem; }
    .modal .err { color: var(--color-error, #d93025); font-size: 0.78rem; margin: 0.2rem 0 0; }
    .modal .hint { font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 0.25rem; }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _loading = false;
  @state() private _error = '';
  @state() private _groups: CoaGroup[] = [];
  @state() private _accountTypes: AccountingAccountType[] = [];
  @state() private _form: AccountForm | null = null;
  @state() private _saving = false;
  @state() private _formError = '';

  private _view = new OutletViewCache<KeptCoaView>(this, 'chart-of-account', {
    capture: () => ({ groups: this._groups }),
    restore: (v) => { this._groups = v.groups; },
    reset: () => { this._groups = []; },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    // switchTo() returns 'unchanged' | 'restored' | 'fresh' — compare, never
    // truthy-test it, or the loader re-runs on every render (endless loop).
    const switched = this._view.switchTo(this.storeCode);
    if (changed.has('storeCode')) {
      // An open editor belongs to the outlet being left.
      this._form = null;
      this._formError = '';
    }
    if (switched === 'unchanged' && !changed.has('storeCode')) return;
    void this._load();
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.accountingMeta(this.storeCode);
      if (!this._view.isCurrent(epoch)) return;
      this._accountTypes = res.data.accountTypes || [];
      const buckets = new Map<string, AccountingAccountRef[]>();
      for (const a of res.data.accounts || []) {
        const key = a.groupName || 'Other';
        const rows = buckets.get(key);
        if (rows) rows.push(a);
        else buckets.set(key, [a]);
      }
      const rank = (n: string) => {
        const i = GROUP_ORDER.indexOf(n);
        return i < 0 ? GROUP_ORDER.length : i;
      };
      this._groups = [...buckets.entries()]
        .map(([name, rows]) => ({
          name,
          rows: [...rows].sort(
            (a, b) =>
              (a.typeName || '').localeCompare(b.typeName || '') ||
              (a.name || '').localeCompare(b.name || ''),
          ),
        }))
        .sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load chart of accounts';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  /** Writable categories grouped for the Add form's <optgroup>s. */
  private get _writableTypeGroups(): Array<[string, AccountingAccountType[]]> {
    const buckets = new Map<string, AccountingAccountType[]>();
    for (const t of this._accountTypes) {
      if (t.writable === false) continue;
      const g = t.groupName || 'Other';
      const rows = buckets.get(g);
      if (rows) rows.push(t);
      else buckets.set(g, [t]);
    }
    const rank = (n: string) => {
      const i = GROUP_ORDER.indexOf(n);
      return i < 0 ? GROUP_ORDER.length : i;
    };
    return [...buckets.entries()].sort(
      (a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]),
    );
  }

  private _typeLabel(typeId: string): string {
    const t = this._accountTypes.find((x) => x.id === typeId);
    if (!t) return '—';
    return t.groupName ? `${t.groupName} — ${t.name}` : t.name;
  }

  private _openAdd() {
    const first = this._writableTypeGroups[0]?.[1][0];
    this._formError = '';
    this._form = { id: '', typeId: first?.id || '', code: '', name: '' };
  }

  private _openEdit(r: AccountingAccountRef) {
    this._formError = '';
    this._form = { id: r.id, typeId: r.typeId || '', code: r.code || '', name: r.name || '' };
  }

  private async _saveForm() {
    const f = this._form;
    if (!f) return;
    const name = f.name.trim();
    const code = f.code.trim();
    if (!f.id && !f.typeId) {
      this._formError = 'Choose an account category';
      return;
    }
    if (!name) {
      this._formError = 'Account name is required';
      return;
    }
    this._saving = true;
    this._formError = '';
    try {
      if (f.id) {
        // Empty code on edit keeps the current one (gateway falls back).
        await api.accountingAccountUpdate(this.storeCode, f.id, { code, name });
      } else {
        await api.accountingAccountCreate(this.storeCode, { typeId: f.typeId, code: code || name, name });
      }
      this._form = null;
      await this._load();
    } catch (err) {
      this._formError = err instanceof Error ? err.message : 'Save failed';
    } finally {
      this._saving = false;
    }
  }

  private async _deleteAccount(r: AccountingAccountRef) {
    if (!window.confirm(`Delete account "${r.name || r.code}"?\n\nThe account disappears from the chart and reports; journal entries are kept.`))
      return;
    try {
      await api.accountingAccountDelete(this.storeCode, r.id);
      await this._load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  private _renderForm() {
    const f = this._form;
    if (!f) return '';
    const editing = !!f.id;
    return html`
      <div class="modal-overlay open" @click=${() => { if (!this._saving) this._form = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${editing ? 'Edit Account' : 'Add Account'}</h3>
            <button class="close" @click=${() => (this._form = null)}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="fg">
              <label>Category</label>
              ${editing
                ? html`<div class="ro">${this._typeLabel(f.typeId)}</div>`
                : html`
                    <select .value=${f.typeId} @change=${(e: Event) => {
                      f.typeId = (e.target as HTMLSelectElement).value;
                      this.requestUpdate();
                    }}>
                      ${this._writableTypeGroups.map(
                        ([group, types]) => html`
                          <optgroup label=${group}>
                            ${types.map(
                              (t) => html`<option value=${t.id} ?selected=${t.id === f.typeId}>${t.name}</option>`,
                            )}
                          </optgroup>`,
                      )}
                    </select>
                    <p class="hint">The category cannot be changed after the account is created.</p>`}
            </div>
            <div class="fg">
              <label>Code</label>
              <input .value=${f.code} placeholder=${f.name ? f.name : 'e.g. CASH'}
                @input=${(e: InputEvent) => (f.code = (e.target as HTMLInputElement).value)} />
            </div>
            <div class="fg">
              <label>Name</label>
              <input .value=${f.name} placeholder="e.g. Petty Cash"
                @input=${(e: InputEvent) => (f.name = (e.target as HTMLInputElement).value)} />
            </div>
            ${this._formError ? html`<p class="err">${this._formError}</p>` : ''}
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" ?disabled=${this._saving} @click=${() => (this._form = null)}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._saving} @click=${() => void this._saveForm()}>
              ${this._saving ? 'Saving…' : editing ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.storeCode) return html`<p>Select a store first.</p>`;
    const canEdit = this.isAdmin;
    return html`
      <div class="head">
        <h2>📒 Chart Of Account</h2>
        <p class="hint">
          ${canEdit
            ? 'Changes reach the desktop app and reports immediately.'
            : 'Accounts are managed by an admin of this outlet.'}
        </p>
        <span class="spacer"></span>
        ${canEdit
          ? html`<button class="btn btn-sm" @click=${() => this._openAdd()}>+ Add Account</button>`
          : ''}
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}
      <div class="body">
        ${this._loading ? html`<div class="state">Loading accounts…</div>` : ''}
        ${!this._loading && this._groups.length === 0
          ? html`<div class="state">No accounts in the chart of accounts.</div>`
          : ''}
        ${this._groups.map(
          (g) => html`
            <div class="group">
              <h3>${g.name}</h3>
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th><th>Account</th><th>Type</th>
                      ${canEdit ? html`<th></th>` : ''}
                    </tr>
                  </thead>
                  <tbody>
                    ${g.rows.map((r) => {
                      const writable = canEdit && r.writable !== false;
                      return html`
                        <tr>
                          <td class="code">${r.code || '—'}</td>
                          <td class="acct">${r.name || '—'}<span class="id">#${r.id}</span></td>
                          <td>${r.typeName || '—'}</td>
                          ${canEdit
                            ? html`
                                <td>
                                  ${writable
                                    ? html`
                                        <div class="actions">
                                          <button class="btn-sm" @click=${() => this._openEdit(r)}>Edit</button>
                                          <button class="btn-sm btn-danger" @click=${() => void this._deleteAccount(r)}>Delete</button>
                                        </div>`
                                    : html`<span class="id">read-only category</span>`}
                                </td>`
                            : ''}
                        </tr>`;
                    })}
                  </tbody>
                </table>
              </div>
            </div>`,
        )}
      </div>
      ${this._renderForm()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chart-of-account': ChartOfAccount;
  }
}
